import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useCallback, useState, useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { getCurrentWeek, logger } from '../../../../Util';
import DataSource from '../../../../api';
import {
  CoachingDetailsInput,
  CoachingFormData,
  CoachingFormInputData,
  ContactInput,
  ContactStatusOptionsObject,
  defaultCoachingFormData,
  StatusOptionsObject,
} from '../../../../api/CoachingForm/schema';
import {
  ConsolidatedContact,
  ContactDetailsInput,
  ContactRecommendationsData,
  RecommendedContactStatusOptionsObject,
  ContactViewTabType,
  ContactViewTabObject,
} from '../../../../api/CoachingNotes/schema';
import DataState from '../../../../Types/DataState';
import { renderUrl, urls } from '../../../../config/Url';
import {
  getTopicByMetric,
  maxCoachingDetail,
} from '../../../CoachingForm/CoachingDetails/Data';
import { StateContext } from '../../../../GlobalStateStore';
import { EmptyCoachingFormInputData } from '../../../../Types/CoachingForm';
import Box from '@amzn/meridian/box';
import CoachingFormDisplay from './CoachingFormDisplay';
import ContactListeningHeader from '../../../ContactListening/ContactListeningHeader';
import {
  ContactData,
  defaultContactData,
} from '../../../../api/ContactListening/schema';
import { getDateRangeFromTimestamp } from '../../../../Util/Week';
import { defaultCLClickstream } from '../../../ContactListening/utils/ContactListeningHelpers';
import { QueryVariablesContactListeningData } from '../../../../api/Naws';
import PerformancePortalContext from '../../../PerformancePortalContext';

export interface ContactModalFormProps {
  parentValues: ContactRecommendationsData | ConsolidatedContact;
  initialValues: ContactDetailsInput;
  action: string;
  parentUserName: string;
  parentUserID: string;
  contactTab: ContactViewTabType;
  sourcePage?: string;
}

const ReviewModalForm: React.FC<ContactModalFormProps> = ({
  parentValues,
  initialValues,
  action,
  parentUserName,
  parentUserID,
  contactTab,
  sourcePage,
}: ContactModalFormProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const onClose = useCallback(() => setOpen(false), []);

  const [contactListeningData, setContactListeningData] =
    useState<ContactData>(defaultContactData);
  const [contactListeningDataState, setContactListeningDataState] =
    useState<string>(DataState.INITIALIZED);
  const [contactListeningParams, setContactListeningParams] =
    useState<QueryVariablesContactListeningData>();
  const { contactStartDate, contactEndDate } = getDateRangeFromTimestamp(
    parentValues.contactDate,
    parentValues.contactDate
  );
  const { userId } = useParams() as { userId: string };
  const navigate = useNavigate();

  const { clearPerformancePortalContextValue } = useContext(
    PerformancePortalContext
  );
  const globalState = useContext(StateContext);
  const inspireOptions = globalState.state.inspireOptions;
  const { cscEntityPrefix } = inspireOptions.data;

  const [singleCoachingNote, setSingleCoachingNote] =
    useState<CoachingFormData>(defaultCoachingFormData);

  const [singleCoachingNoteState, setSingleCoachingNoteState] = useState(
    DataState.LOADING
  );
  const [coachingNote, setCoachingNote] = useState<CoachingFormData>();
  const [coachingNoteState, setCoachingNoteState] = useState(DataState.LOADING);
  const [isCRFull, setIsCRFull] = useState(false);

  const intl = useIntl();
  const { contactID } = parentValues;
  const apiNaws = DataSource.getDataFeed('NAWS');

  const queryVariables = apiNaws.prepareContactQuery(
    parentValues.deliverInfo?.noteID,
    userId || null,
    cscEntityPrefix
  );

  const actionTranslationId =
    (action === 'Review' && 'cs_perfportal_contact_recom_action_review') ||
    (action === 'View' && 'cs_perfportal_contact_recom_action_view') ||
    (action === 'Edit' && 'cs_perfportal_contact_recom_action_edit');

  const onClickCoachingNote = useCallback(() => {
    apiNaws.getSingleCoachingNote(
      queryVariables,
      setSingleCoachingNote,
      setSingleCoachingNoteState
    );
    setOpen(true);
  }, []);

  const openContactReviewPage = useCallback(() => {
    // context is also cleared on top level nav change in PerformancePortalLandingProps
    // theoretically, this will not do anything, but included here as a backstop incase the user ends up on this page via an alternate path
    clearPerformancePortalContextValue();
    navigate(renderUrl(`${urls.contactReview}/${userId}/${contactID}`));
  }, []);

  const contactCategory =
    parentValues.category &&
    parentValues.category.toLowerCase().startsWith('kudos')
      ? 'Kudos'
      : 'Corrective';
  const copyDraft = (
    draftCoachingNote: CoachingFormData,
    initialCoachingNote: CoachingFormInputData
  ): void => {
    initialCoachingNote.id = draftCoachingNote.id;
    initialCoachingNote.recipient = draftCoachingNote.recipient;
    initialCoachingNote.contactReview = draftCoachingNote.contactReview;
    initialCoachingNote.coachingDetails = draftCoachingNote.coachingDetails;
    initialCoachingNote.coachingSession = draftCoachingNote.coachingSession;
    initialCoachingNote.otherReason = draftCoachingNote.otherReason;
    initialCoachingNote.reason = draftCoachingNote.reason;
  };

  const handleFormCreation = async (
    values: ContactDetailsInput,
    draft: CoachingFormData,
    userid: string,
    username: string,
    contact: ConsolidatedContact
  ): Promise<void> => {
    const initialValues: CoachingFormInputData = {
      ...EmptyCoachingFormInputData,
    };
    initialValues.recipient = {
      userID: `${cscEntityPrefix}:${userid}`,
      userName: `${username}`,
    };

    const contactInfo: ContactInput = {
      cscContactID: contact.contactID,
      followedSecurityPolicies: values.followedSecurityPolicies ?? '',
      content: values.content ?? '',
      vertical: values.vertical ?? '',
      relatedMetric: values.relatedMetric!,
      marketplaceID: contact.marketplaceID,
      marketplaceDomainName: contact.marketplaceDomainName,
      contactTime: {
        calWeek: contact.calWeek,
        calYear: contact.calYear,
      },
    };
    const topic = getTopicByMetric(values.relatedMetric!);
    const coachingDetailsValues: CoachingDetailsInput = {
      coachingTopic: topic ? topic : '',
      relatedContacts: [contact.contactID],
      tenets: [],
      behaviors: [],
      relatedMetric: values.relatedMetric!,
      assignedAction: '',
      status: StatusOptionsObject.PENDING,
    };
    if (draft.id) {
      copyDraft(draft, initialValues);
    }
    initialValues.contactReview.push(contactInfo);
    if (draft.coachingDetails.length < maxCoachingDetail) {
      initialValues.coachingDetails.push(coachingDetailsValues);
    }
    await apiNaws.updateCoachingNote(initialValues, (state: string) => {
      if (state === DataState.ERROR) {
        logger.error('Failed to update coaching note');
      }
    });
  };

  const navigateToCoaching = (apeendCoachingDetail: boolean) => {
    if (apeendCoachingDetail) {
      navigate(renderUrl(`${urls.coachingForm}/${userId}?isEdit=true`), {
        replace: true,
      });
    } else {
      navigate(renderUrl(`${urls.coachingForm}/${userId}`), { replace: true });
    }
  };

  const handleAdd = async (
    contact: ConsolidatedContact,
    draft: CoachingFormData,
    contactInfo: ContactDetailsInput
  ): Promise<void> => {
    await handleFormCreation(
      contactInfo,
      draft,
      userId,
      parentUserName,
      contact
    );
    navigateToCoaching(draft.coachingDetails.length < maxCoachingDetail);
  };

  const handleAddFromTable = async (contact: ConsolidatedContact) => {
    if (!userId) {
      return;
    }

    let draft: any;
    const queryVariablesNaws = apiNaws.prepareQuery(
      getCurrentWeek(),
      userId,
      cscEntityPrefix
    );
    await apiNaws.getDraftCoachingNote(
      queryVariablesNaws,
      (draftResp: any) => {
        draft = draftResp;
      },
      (draftState: string) => {
        if (draftState === DataState.ERROR) {
          logger.error('Failed to retrieve coaching draft');
        }
      }
    );

    await handleAdd(contact, draft, contact.contactInfo as ContactDetailsInput);
  };

  useEffect(() => {
    if (
      contactTab === ContactViewTabObject.SAVED &&
      inspireOptions.state === DataState.LOADED &&
      userId
    ) {
      const queryCoachVariables = apiNaws.prepareQuery(
        getCurrentWeek(),
        userId,
        cscEntityPrefix
      );
      apiNaws.getDraftCoachingNote(
        queryCoachVariables,
        setCoachingNote,
        setCoachingNoteState
      );
    }
  }, [inspireOptions.state, userId, contactTab]);

  useEffect(() => {
    if (coachingNoteState === DataState.LOADED) {
      if (
        coachingNote?.contactReview &&
        coachingNote?.contactReview.length >= 3
      ) {
        setIsCRFull(true);
      }
    }
  }, [coachingNoteState, coachingNote]);

  return (
    <Row>
      {contactTab === ContactViewTabObject.SAVED &&
        coachingNoteState === DataState.LOADED &&
        !isCRFull &&
        parentValues.status === 'REVIEWED' && (
          <Box spacingInset='none 300 none none'>
            <Link
              data-csa-c-type='link'
              data-csa-c-slot-id='contact-table-modal-add-to-coaching-link-slot'
              data-csa-c-content-id='contact-table-modal-add-to-coaching-link'
              data-csa-c-tag1={sourcePage || ''}
              data-csa-c-tag2='Contact Table'
              data-csa-c-tag3={contactTab ? `${contactTab}` : ''}
              data-csa-c-contactid={contactID}
              data-csa-c-profile-id={userId}
              data-csa-c-category={contactCategory}
              type='secondary'
              onClick={() =>
                handleAddFromTable(parentValues as ConsolidatedContact)
              }
            >
              <FormattedMessage
                id='cs_perfportal_add_to_coaching'
                defaultMessage='Add to coaching'
              />
            </Link>
          </Box>
        )}
      <Link
        data-csa-c-type='link'
        data-csa-c-slot-id={`contact-table-modal-${action}-action-link-slot`}
        data-csa-c-content-id={`contact-table-modal-${action}-action-link`}
        data-csa-c-action={`${action}`}
        data-csa-c-tag1={sourcePage || ''}
        data-csa-c-tag2='Contact Table'
        data-csa-c-tag3={contactTab ? `${contactTab}` : ''}
        data-csa-c-contactid={contactID}
        data-csa-c-profile-id={userId}
        data-csa-c-category={contactCategory}
        data-cy={`contact-table-modal-${action}-action-link-${contactID}`}
        onClick={
          (parentValues as ConsolidatedContact).status !==
            RecommendedContactStatusOptionsObject.COACHED &&
          (parentValues as ConsolidatedContact).status !==
            RecommendedContactStatusOptionsObject.PENDINGCOACHING
            ? openContactReviewPage
            : onClickCoachingNote
        }
        type='secondary'
        disabled={
          (parentValues as ConsolidatedContact).contactStatus !== null &&
          (parentValues as ConsolidatedContact).contactStatus !==
            ContactStatusOptionsObject.DRAFT &&
          action === 'Edit'
        }
      >
        {actionTranslationId && (
          <FormattedMessage
            id={actionTranslationId}
            defaultMessage={action}
            description={`${action} Action`}
          />
        )}
      </Link>
      <Modal
        title={
          action === 'Review'
            ? intl.formatMessage(
                {
                  id: 'cs_perfportal_contact_recom_modal_title_for_contacID',
                  defaultMessage: 'New review for contact {contactID}',
                },
                {
                  contactID,
                }
              )
            : intl.formatMessage(
                {
                  id: 'cs_perfportal_contact_recom_modal_title_for_view',
                  defaultMessage:
                    'Viewing coaching details for contact {contactID}',
                },
                {
                  contactID,
                }
              )
        }
        open={open}
        onClose={onClose}
        scrollContainer='viewport'
        closeLabel='Close'
        aria-describedby='modal-contact-recommendation-form'
      >
        <Column spacing='300'>
          <Row minWidth='800px'>
            <ContactListeningHeader
              contactID={parentValues.contactID}
              contactListening={contactListeningData}
              contactListeningState={contactListeningDataState}
              setContactListening={setContactListeningData}
              setContactListeningState={setContactListeningDataState}
              setContactListeningParams={setContactListeningParams}
              contactStartDate={contactStartDate}
              contactEndDate={contactEndDate}
              CLClickstream={defaultCLClickstream}
            />
          </Row>
          <Row>
            <Text>
              {[parentValues.skill, parentValues.sic, parentValues.category]
                .filter(item => !!item)
                .join(', ')}
            </Text>
          </Row>

          {singleCoachingNoteState === DataState.LOADING && (
            <div style={{ textAlign: 'center' }}>
              <Loader type='circular' size='large' />
            </div>
          )}
          {singleCoachingNoteState === DataState.LOADED && (
            <Row>
              <CoachingFormDisplay
                parentValues={parentValues}
                coachingNote={singleCoachingNote}
              />
            </Row>
          )}
        </Column>
      </Modal>
    </Row>
  );
};
export default ReviewModalForm;
